/**
 * ESPEJO del catálogo del server (`server/src/domain/catalogs/campos-modificables.catalog.ts`).
 *
 * MANTENER SINCRONIZADO. `campos-modificables.spec.ts` compara las claves y la
 * sensibilidad contra esta copia; si el server agrega un campo y acá no está,
 * el test falla. Es la regla #3 del proyecto (listas paralelas): el mirror del
 * validador de asociados ya driftó tres veces por no tener ese amarre.
 *
 * Acá NO va la lógica de validación —eso lo decide el server, que es el único
 * que ve el padrón—; solo lo que la pantalla necesita para pintar.
 */

export const CLASES_MODIFICACION = ['sensibles', 'no_sensibles'] as const;
export type ClaseModificacion = (typeof CLASES_MODIFICACION)[number];

export type Sensibilidad = 'sensible' | 'no_sensible';

export interface CampoModificableUi {
  header: string;
  label: string;
  sensibilidad: Sensibilidad;
  /** Viaja en el archivo pero no modifica nada (hoy solo `sexo`, para RENAPER). */
  soloConsulta?: boolean;
}

export const CAMPOS_MODIFICABLES: Record<string, CampoModificableUi> = {
  cuil: { header: 'CUIL NUEVO', label: 'CUIL', sensibilidad: 'sensible' },
  tipoDoc: { header: 'TIPO DOC', label: 'Tipo de documento', sensibilidad: 'sensible' },
  documento: { header: 'DOCUMENTO', label: 'Nº de documento', sensibilidad: 'sensible' },
  apellSolt: { header: 'APELLIDO', label: 'Apellido', sensibilidad: 'sensible' },
  nombres: { header: 'NOMBRES', label: 'Nombres', sensibilidad: 'sensible' },
  fechaNacimiento: { header: 'F. NAC', label: 'Fecha de nacimiento', sensibilidad: 'sensible' },
  parentesco: { header: 'PARENTESCO', label: 'Parentesco', sensibilidad: 'sensible' },
  sexo: { header: 'SEXO', label: 'Sexo', sensibilidad: 'sensible', soloConsulta: true },
  email: { header: 'EMAIL', label: 'Email', sensibilidad: 'no_sensible' },
  areaCelular: { header: 'COD. AREA', label: 'Código de área', sensibilidad: 'no_sensible' },
  numeroCelular: { header: 'CELULAR', label: 'Nº de celular', sensibilidad: 'no_sensible' },
  estadoCivil: { header: 'EST. CIVIL', label: 'Estado civil', sensibilidad: 'no_sensible' },
};

export function labelCampo(campo: string): string {
  return CAMPOS_MODIFICABLES[campo]?.label ?? campo;
}

export function etiquetaClase(clase: ClaseModificacion): string {
  return clase === 'sensibles' ? 'Datos Sensibles' : 'Datos No Sensibles';
}

// ───────────────────────── estados de una novedad ─────────────────────────

/** Espejo de `ESTADOS_MODIFICACION` del server. */
export const ESTADOS_MODIFICACION = [
  'aceptada',
  'sin_cambio',
  'solo_consulta',
  'rechazada_formato',
  'rechazada_transicion',
  'rechazada_fuera_alcance',
  'rechazada_no_encontrado',
  'rechazada_ambigua',
  'rechazada_estado',
  'rechazada_renaper',
  'duplicada_en_archivo',
  'excluida_por_usuario',
  'error_infra',
  'enviando',
  'aplicada',
  'fallada',
] as const;

export type ModificacionEstado = (typeof ESTADOS_MODIFICACION)[number];

/**
 * Cómo se pinta cada estado. El `tono` agrupa por lo que el usuario tiene que
 * HACER, no por severidad técnica:
 *  - `ok`     → se va a enviar,
 *  - `neutro` → no hace falta hacer nada (ya estaba así, o es auxiliar),
 *  - `error`  → hay que corregir el archivo,
 *  - `espera` → no es culpa del dato, se puede reintentar.
 *
 * Los colores son los mismos que usa el chip de Bajas, para que las dos
 * pantallas de carga masiva se lean igual.
 */
export interface EstadoUi {
  label: string;
  tono: 'ok' | 'neutro' | 'error' | 'espera';
  color: string;
  bg: string;
}

const VERDE = { color: '#166534', bg: '#e2f3e8' };
const GRIS = { color: '#6b7280', bg: '#f1f3f5' };
const ROJO = { color: '#b91c1c', bg: '#f9e2e5' };
const AMBAR = { color: '#b45309', bg: '#f8ecd2' };

export const ESTADO_UI: Record<ModificacionEstado, EstadoUi> = {
  aceptada: { label: 'Lista para enviar', tono: 'ok', ...VERDE },
  sin_cambio: { label: 'Sin cambio', tono: 'neutro', ...GRIS },
  solo_consulta: { label: 'Solo consulta', tono: 'neutro', ...GRIS },
  rechazada_formato: { label: 'Formato inválido', tono: 'error', ...ROJO },
  rechazada_transicion: { label: 'Cambio no permitido', tono: 'error', ...ROJO },
  rechazada_fuera_alcance: { label: 'Fuera de alcance', tono: 'error', ...ROJO },
  rechazada_no_encontrado: { label: 'No encontrada', tono: 'error', ...ROJO },
  rechazada_ambigua: { label: 'CUIL ambiguo', tono: 'error', ...ROJO },
  rechazada_estado: { label: 'No modificable', tono: 'error', ...ROJO },
  rechazada_renaper: { label: 'RENAPER no confirma', tono: 'error', ...ROJO },
  duplicada_en_archivo: { label: 'Duplicada', tono: 'error', ...ROJO },
  excluida_por_usuario: { label: 'Excluida', tono: 'neutro', ...GRIS },
  error_infra: { label: 'No se pudo validar', tono: 'espera', ...AMBAR },
  // No es reintentable sola: quedó mandada a Binary sin respuesta y hay que
  // verificar allá antes de volver a enviarla.
  enviando: { label: 'Enviada, sin respuesta', tono: 'espera', ...AMBAR },
  aplicada: { label: 'Aplicada', tono: 'ok', ...VERDE },
  fallada: { label: 'Rechazada por Binary', tono: 'error', ...ROJO },
};

export function estadoUi(estado: string): EstadoUi {
  return ESTADO_UI[estado as ModificacionEstado] ?? { label: estado, tono: 'neutro', ...GRIS };
}
